import React, { useMemo } from 'react';
import {
	Box,
	Button,
	Chip,
	IconButton,
	Tooltip,
	Typography,
} from '@mui/material';
import { AttachFile as AttachIcon } from '@mui/icons-material';
import { DeleteIcon, UploadIcon } from 'components/icons';
import UppyDashboard from 'components/Uppy/UppyDashboard';
import DeleteAttachmentDialog from 'containers/pages/cases/components/caseAttachments/DeleteAttachmentDialog';
import useCommentAttachments from '../../hooks/useCommentAttachments';

interface CommentAttachmentsProps {
	commentId: number;
	caseId: number;
	customerId: number;
}

function formatFileSize(bytes: number): string {
	if (bytes === 0) return '0 B';
	const units = ['B', 'KB', 'MB', 'GB'];
	const i = Math.floor(Math.log(bytes) / Math.log(1024));
	return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function CommentAttachments({ commentId, caseId, customerId }: CommentAttachmentsProps) {
	const { state, handler } = useCommentAttachments({ commentId, caseId, customerId });

	const uploadMeta = useMemo(() => ({
		CustomerId: customerId,
		CaseId: caseId,
		CommentId: commentId,
	}), [customerId, caseId, commentId]);

	return (
		<Box sx={{ mt: 1 }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
				<AttachIcon fontSize="small" color="action" />
				<Typography variant="caption" color="text.secondary">
					Attachments ({state.attachments.length})
				</Typography>
				<Button size="small" startIcon={<UploadIcon />} onClick={handler.openUploadModal}>
					Upload
				</Button>
			</Box>

			{state.attachments.length > 0 && (
				<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
					{state.attachments.map((a) => (
						<Chip
							key={a.Id}
							label={`${a.FileName} (${formatFileSize(a.FileSize)})`}
							component="a"
							href={a.Url}
							target="_blank"
							rel="noopener noreferrer"
							clickable
							size="small"
							variant="outlined"
							onDelete={() => handler.openDeleteDialog(a)}
							deleteIcon={
								<Tooltip title="Delete">
									<DeleteIcon fontSize="small" />
								</Tooltip>
							}
						/>
					))}
				</Box>
			)}

			<UppyDashboard
				metaData={uploadMeta}
				onSingleUploadSuccess={handler.onUploadSuccess}
				open={state.uploadModalOpen}
				handleClose={handler.closeUploadModal}
			/>

			<DeleteAttachmentDialog
				open={state.deleteTarget !== null}
				onClose={handler.closeDeleteDialog}
				onConfirm={handler.confirmDelete}
				fileName={state.deleteTarget?.FileName || ''}
			/>
		</Box>
	);
}

export default CommentAttachments;
